import { useContext, useMemo } from "react";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { DisplayInOptionsEnum, SummaryOption } from "constants/constants";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useStyles } from "../../DowntimeSummaryByFailureType/DowntimeSummaryByFailureType.styles";
import { IDowntimeTotalsSummarized } from "../../TotalDowntimeByMachineCenter/TotalDowntimeByMachineCenterInterfaces";
import { percentageChecker } from "utilities/utilities";
import NoData from "components/NoData/NoData.view";
import { CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import SummaryReportCustomBarComponent from "../../SummaryReportCustomBarComponent";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { IMillReportCardProps } from "pages/Corporate/Locations/MillDashboards/MillDashboardInterfaces";

const DowntimeFailureCodeByMachineCenter = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const { millId } = useParams();
    const { selectedFailureType, selectedFailureCode, filters, totalDowntimeSeconds } =
        useContext(SummaryReportDashboardContext);

    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const { downtimeSummaryPerMachineCenter, loading } =
        MillsEntity.GetTotalDowntimeSummaryByCodePerMachineCenterMillDashboard(
            { ...filters, startDate, endDate, displayIn },
            SummaryOption.BY_CODE,
            +millId!,
            Number(selectedFailureCode?.id),
        );

    const downtimeSummaryFormatted = useMemo(
        () =>
            downtimeSummaryPerMachineCenter.reduce((acc: IDowntimeTotalsSummarized[], obj) => {
                const machineCenterId = obj.machine_center.machine_center_id;
                const existingMachineCenterIndex = acc.findIndex(
                    (summary) => summary.machineCenterId === machineCenterId,
                );

                if (!(existingMachineCenterIndex >= 0)) {
                    acc = [
                        ...acc,
                        {
                            machineCenterId: obj.machine_center.machine_center_id,
                            name: obj.machine_center.short_name || obj.machine_center.name,
                            typeTotals: [
                                {
                                    name: obj.failure_data.name,
                                    color_code: selectedFailureType?.color_code || "",
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                },
                            ],
                        },
                    ];
                } else {
                    const existingFailureTypeIndex = acc[existingMachineCenterIndex].typeTotals.findIndex(
                        (summary) => summary.name === obj.failure_data.name,
                    );

                    if (!(existingFailureTypeIndex >= 0)) {
                        acc[existingMachineCenterIndex] = {
                            ...acc[existingMachineCenterIndex],
                            typeTotals: [
                                ...acc[existingMachineCenterIndex].typeTotals,
                                {
                                    name: obj.failure_data.name,
                                    color_code: selectedFailureType?.color_code || "",
                                    total_seconds: obj.total_seconds,
                                    total_occurrences: obj.total_occurrences,
                                },
                            ],
                        };
                    } else {
                        acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex] = {
                            ...acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex],
                            total_seconds:
                                acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex].total_seconds +
                                obj.total_seconds,
                            total_occurrences:
                                acc[existingMachineCenterIndex].typeTotals[existingFailureTypeIndex].total_occurrences +
                                obj.total_occurrences,
                        };
                    }
                }
                return acc;
            }, []),
        [downtimeSummaryPerMachineCenter, selectedFailureType?.color_code],
    );

    const graphData = useMemo(
        () =>
            downtimeSummaryFormatted
                .map((downtimeSummary) => {
                    let data: { [key: string]: string | number } = {
                        machineCenterId: downtimeSummary.machineCenterId,
                        machineCenter: downtimeSummary.name,
                        countryCode: countryCode as string,
                        numberId: downtimeSummary.machineCenterId,
                    };
                    downtimeSummary.typeTotals.forEach((total) => {
                        const secondsToMinutes =
                            Math.round(total.total_seconds / 60) < 1 ? 1 : Math.round(total.total_seconds / 60);
                        data = {
                            ...data,
                            [`${total.name}`]:
                                displayIn === DisplayInOptionsEnum.MINUTES
                                    ? secondsToMinutes
                                    : displayIn === DisplayInOptionsEnum.PERCENT
                                    ? totalDowntimeSeconds
                                        ? (100 * total.total_seconds) / totalDowntimeSeconds
                                        : 0
                                    : total.total_occurrences,
                            [`${total.name}Color`]: total.color_code,
                        };
                    });

                    const totalDowntime = downtimeSummary.typeTotals.reduce(
                        (acc, obj) =>
                            displayIn === DisplayInOptionsEnum.MINUTES
                                ? (Math.round(obj.total_seconds / 60) < 1 ? 1 : Math.round(obj.total_seconds / 60)) +
                                  acc
                                : displayIn === DisplayInOptionsEnum.PERCENT
                                ? (totalDowntimeSeconds ? (100 * obj.total_seconds) / totalDowntimeSeconds : 0) + acc
                                : obj.total_occurrences + acc,
                        0,
                    );
                    data = {
                        ...data,
                        totalDowntimeByMill:
                            displayIn === DisplayInOptionsEnum.PERCENT
                                ? percentageChecker(totalDowntime)
                                : totalDowntime,
                    };

                    return data;
                })
                .sort((a, b) => Number(a.totalDowntimeByMill) - Number(b.totalDowntimeByMill))
                .slice(-5),
        [downtimeSummaryFormatted, totalDowntimeSeconds, displayIn, countryCode],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeBarChart
            customBarComponent={SummaryReportCustomBarComponent}
            data={graphData}
            noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
            index="machineCenter"
            noTrend
            noLegend
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeFailureCodeByMachineCenter;
